import { localStorageClient } from '../../clients/local-storage-client';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { AuthTokenPayload } from './types';
import { env } from 'env';
import { sessionClientHeaders } from 'auth/session-client';

// Access token lives in memory only — never persisted to localStorage.
// Cleared on page unload; restored via the HttpOnly refresh-token cookie.
let _accessToken: string | null = null;

const USERNAME_KEY = 'auth:username';
const baseUrl = env.REACT_APP_API_URL || '/api';
export const AUTH_TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';

let refreshInflight: Promise<string | null> | null = null;

// Exposed so the API client can wait for an in-progress restore before
// sending a request without a token.
export function getRefreshInflight(): Promise<string | null> | null {
  return refreshInflight;
}

export async function getToken(): Promise<string | null> {
  return _accessToken;
}

export async function setToken(token: string): Promise<void> {
  _accessToken = token;
}

export async function clearToken(): Promise<void> {
  _accessToken = null;
}

export async function refreshAccessToken(
  silent = false,
): Promise<string | null> {
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/auth') &&
    localStorage.getItem('loginpage') == null
  ) {
    return null;
  }
  localStorage.removeItem('loginpage');
  if (refreshInflight) {
    return refreshInflight;
  }

  refreshInflight = (async () => {
    try {
      const {
        data: { data },
      } = await axios.post(
        `${baseUrl}/UserManagement/refresh-access-token`,
        {},
        { withCredentials: true },
      );

      await setToken(data.accessToken);

      const tokenPayload = decodeToken(data.accessToken);
      const expirationDate = new Date(tokenPayload.exp * 1000);
      const now = new Date();
      const expiresIn = (expirationDate.getTime() - now.getTime()) / 1000;
      const passwordExpiration = data.passwordExpiration
        ? new Date(data.passwordExpiration)
        : null;

      const authData = {
        accessToken: data.accessToken,
        expiresIn,
        hasTemporaryPassword: !!passwordExpiration && passwordExpiration < now,
        passwordExpiration,
        isExpiredAccountByDate: data.isExpiredAccountByDate ?? null,
        agencyGroup: data.agencyGroup ?? null,
        agencyGroupId: data.agencyGroupId ?? null,
      };

      if (!silent) {
        window.dispatchEvent(
          new CustomEvent(AUTH_TOKEN_REFRESHED_EVENT, {
            detail: authData,
          }),
        );
      }

      return data.accessToken;
    } catch {
      await clearToken();
      return null;
    } finally {
      refreshInflight = null;
    }
  })();

  return refreshInflight;
}

// The two "complete" calls are what actually open the session when the user has MFA: the login only
// returns a challenge token. That is why they carry the same application identity as the login - if
// it were set on the login alone, most sessions would be stored with no known application.
export async function executeEnrollmentComplete(
  enrollmentToken: string,
): Promise<any> {
  const response = await axios.post(
    `${baseUrl}/mfa/enrollment/complete`,
    {},
    {
      headers: {
        Authorization: `Bearer ${enrollmentToken}`,
        ...sessionClientHeaders,
      },
      withCredentials: true,
    },
  );

  return response.data;
}

export async function executeLoginComplete(mfaToken: string): Promise<any> {
  const response = await axios.post(
    `${baseUrl}/mfa/login/complete`,
    {},
    {
      headers: {
        Authorization: `Bearer ${mfaToken}`,
        ...sessionClientHeaders,
      },
      withCredentials: true,
    },
  );

  return response.data;
}

export async function getUsername(): Promise<string | null> {
  const username = await localStorageClient.getItem<string>(USERNAME_KEY);
  return username;
}

export async function setUsername(username: string): Promise<void> {
  await localStorageClient.setItem<string>(USERNAME_KEY, username);
}

export async function clearUsername(): Promise<void> {
  await localStorageClient.removeItem(USERNAME_KEY);
}

export function validateToken() {}

export function decodeToken(token: string): AuthTokenPayload {
  const decodedToken = jwtDecode<any>(token);
  const normalizedRole = (
    decodedToken[
      'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
    ] || ''
  )
    .toLowerCase()
    .replace(/\s/gi, '_');

  return {
    username:
      decodedToken[
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
      ],
    role: normalizedRole,
    hasDonorCreateEdit: decodedToken['HasDonorCreateEdit'] === 'True',
    hasCollections: decodedToken['HasCollections'] === 'True',
    results: decodedToken['Results'] === 'True',
    hasCollectionsModule: decodedToken['HasCollectionsModule'] === 'True',
    hasTemporaryPassword: decodedToken['HasTemporaryPassword'] === 'True',
    hasDonorEditAccess: decodedToken['HasDonorEditAccess'] === 'True',
    hasDonorView: decodedToken['HasDonorView'] === 'True',
    hasSchedulingView: decodedToken['HasSchedulingView'] === 'True',
    hasSchedulingCreateEdit: decodedToken['HasSchedulingCreateEdit'] === 'True',
    exp: decodedToken['exp'],
    permissions: {},
    loginUsername: decodedToken['LoginUsername'],
    effectiveUsername: decodedToken['EffectiveUsername'],
  };
}
